import React, { useState } from 'react';
import { Card } from '../../../../../components/Card/Card';
import { t } from '../../../../../locales/translate';
import { KeyValueTable } from '../../../../../components/KeyValueTable/KeyValueTable';
import { Event } from '../../../../../model/event';
import { ControlledModal } from '../../../../../components/Modal/ControlledModal';
import { formatPercentage } from '../../../../../util/util';
import { linesAsBulletpoints } from '../../../../../util/react-util';
import { Label } from '../../../../../components/Form/Label/Label';
import { Product, SHIPPING_REQUIREMENTS } from '../../../../../model/product';
import { Spacer } from '../../../../../components/Spacer/Spacer';
import { Stack } from '../../../../../components/Stack/Stack';
import { Modal } from '../../../../../components/Modal/Modal';
import { camelize } from 'humps';
import { ShippingOptionsForm } from './Modals/ShippingOptionsForm';
import { CartLimitForm } from './Modals/CartLimitForm';
import { PaymentOptionsForm } from './Modals/PaymentOptionsForm';
import { PermissionButton } from '../../../../../components/Button/PermissionButton';

interface Props {
  product: Event | Product;
  productType: 'event' | 'product';
}

export function immediatePaymentSelection(quota: number | null): 'only' | 'all' | 'custom' {
  return quota === null || quota === 1 ? 'all' : quota === 0 ? 'only' : 'custom';
}

export function CheckoutOptions({ product, productType }: Props): JSX.Element {
  const [showEditPaymentModal, setShowEditPaymentsModal] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [showShippingRequirementsModal, setShowShippingRequirementsModal] = useState(false);
  const state = immediatePaymentSelection(product.immediatePaymentAtStockQuota);

  const shippingRequirementsItems = (product.shippingRequirements || []).map((requirement) => {
    if (!SHIPPING_REQUIREMENTS.includes(requirement)) return null;

    const camelizedRequirement = camelize(requirement);
    return {
      key: t.model.shippingRequirements._requirements[camelizedRequirement]?.title,
      value: t.model.shippingRequirements._requirements[camelizedRequirement]?.description,
    };
  });
  const prohibitedShippingTagsItems = (product.prohibitedShippingTags || []).map((tag) => {
    const camelizedTag = camelize(tag);
    const tagTranslations: typeof t.model.shippingTags._tags.warenpost = t.model.shippingTags._tags[camelizedTag];
    return {
      key: tagTranslations?.prohibitedTitle || tag.replaceAll('_', ' '),
      value: tagTranslations?.prohibitedDescription,
    };
  });
  const shippingOptionsItems = shippingRequirementsItems.concat(prohibitedShippingTagsItems);

  return (
    <Card title={t.model.product.checkoutOptions} elevation={1}>
      <Stack>
        <Label
          title={t.model.product.instantPaymentRate}
          actions={
            <PermissionButton
              model={product}
              permission="isAdmin"
              name="edit--sm"
              onClick={() => setShowEditPaymentsModal(true)}
            />
          }
        />
        {state === 'custom' && (
          <>
            <KeyValueTable
              className="ticket-settings__details"
              displayEmpty={true}
              emptyMsg={t.model.event.noPaymentThresholdSet}
              items={[
                {
                  key: t.model.event.paymentThreshold,
                  value: formatPercentage(product.immediatePaymentAtStockQuota),
                },
              ]}
            />
            <div>{linesAsBulletpoints(t.model.event.instantPaymentCustomDescription)}</div>
          </>
        )}
        {state === 'only' && <div>{t.model.event.instantPaymentDescription.only}</div>}
        {state === 'all' && <div>{t.model.event.instantPaymentDescription.all}</div>}

        <Spacer size="none" />

        <Label
          title={t.model.product.cartLimit}
          actions={
            <PermissionButton
              model={product}
              permission="isAdmin"
              name="edit--sm"
              onClick={() => setShowLimitModal(true)}
            />
          }
        />
        <div>{product.cartLimit ? t.model.item.itemCount(product.cartLimit) : '-'}</div>

        {productType === 'product' && (
          <>
            <Spacer size="none" />
            <Label
              title={t.model.shipping}
              actions={
                <PermissionButton
                  model={product}
                  permission="isAdmin"
                  name="edit--sm"
                  onClick={() => setShowShippingRequirementsModal(true)}
                />
              }
            />
            <KeyValueTable emptyMsg={t.model.shippingRequirements.none} labelWidth={120} items={shippingOptionsItems} />
            <KeyValueTable
              labelWidth={120}
              items={[
                {
                  key: t.model.product.freeShippingMode.title,
                  value: product.freeShippingMode
                    ? t.model.product.freeShippingMode[product.freeShippingMode.toLowerCase()]
                    : null,
                },
              ]}
            />
          </>
        )}
      </Stack>

      <ControlledModal
        open={showEditPaymentModal}
        model={{
          value: product.immediatePaymentAtStockQuota,
        }}
        onClose={() => setShowEditPaymentsModal(false)}
        title={t.actions.edit(t.model.event.paymentOptions)}
        size="small"
      >
        {(controller) => (
          <PaymentOptionsForm controller={controller} productId={product.id} productType={productType} />
        )}
      </ControlledModal>
      <ControlledModal
        open={showLimitModal}
        model={{
          value: product.cartLimit,
        }}
        onClose={() => setShowLimitModal(false)}
        title={t.actions.edit('Cart limit')}
        size="xs"
      >
        {(controller) => <CartLimitForm controller={controller} productId={product.id} productType={productType} />}
      </ControlledModal>
      <Modal open={showShippingRequirementsModal} title={t.model.shipping} size="xs">
        <ShippingOptionsForm
          productId={product.id}
          shippingRequirements={product.shippingRequirements || []}
          prohibitedShippingTags={product.prohibitedShippingTags || []}
          freeShippingMode={product.freeShippingMode ?? 'OFF'}
          onClose={() => setShowShippingRequirementsModal(false)}
          productType={productType}
        />
      </Modal>
    </Card>
  );
}
